import React, { useCallback, useEffect, useState } from "react";
import { addChatMessage } from "../store/chat-messages/slice";
import { uuidv4 } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/reducers";
import { isMobile } from "react-device-detect";

export function useChatBox(
  formElement: HTMLFormElement,
  messagesEndRef: React.MutableRefObject<HTMLDivElement>,
  textAreaElement?: HTMLTextAreaElement
) {
  const { localDataTrack } = useSelector((s: RootState) => s.localTracks);
  const { messages, showChat } = useSelector((s: RootState) => s.chatMessages);
  const dispatch = useDispatch();
  const [isBottom, setIsBottom] = useState(true);
  const [newMessage, setNewMessage] = useState("");

  /***The following scroll handlers are to account for a Firefox scrolling bug*/
  const handleScroll = useCallback((e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    bottom ? setIsBottom(true) : setIsBottom(false);
  }, []);

  //there is potential for a `new messages` button with this code
  const scrollToBottom = useCallback(() => {
    if (isBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isBottom, messagesEndRef]);

  useEffect(() => {
    if (!isMobile) scrollToBottom();
  }, [messages, scrollToBottom]);
  /**^^^**/

  useEffect(() => {
    if (textAreaElement) {
      showChat ? textAreaElement.focus() : textAreaElement.blur();
    }
  }, [showChat, textAreaElement]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 13 && e.shiftKey === false) {
        e.preventDefault();
        e.stopPropagation();
        formElement.dispatchEvent(new Event("submit", { cancelable: true }));
      }
    },
    [formElement]
  );

  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
  }, []);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (newMessage) {
        localDataTrack.send(newMessage);
        /*Local participant does not listen to its own messages, so manually add the message here.*/
        dispatch(
          addChatMessage({
            id: uuidv4(),
            sender: "me",
            message: newMessage,
          })
        );
        setNewMessage("");
      }
    },
    [dispatch, localDataTrack, newMessage]
  );

  const onFocus = useCallback((e) => {
    window.scrollTo({
      top: Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      ),
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return {
    onKeyDown,
    onSubmit,
    onChange,
    handleScroll,
    newMessage,
    onFocus,
  };
}
